import { c as _c } from "react/compiler-runtime";
/**
 * Groups flat audit log entries into logical revisions (batches + standalones).
 * Extracted from the original care-plan-log-dialog.tsx for reuse.
 */
import { useMemo } from "react";
import { type CarePlanLogEntry } from "@/api/provider/patient/care-plan";
export type LogGroup = {
  type: "standalone";
  entry: CarePlanLogEntry;
} | {
  type: "batch";
  entries: CarePlanLogEntry[];
  batchId: string;
};
export function groupLogs(logs: CarePlanLogEntry[]): LogGroup[] {
  const batchMap = new Map<string, CarePlanLogEntry[]>();
  const standalone: {
    index: number;
    entry: CarePlanLogEntry;
  }[] = [];
  const batchFirstIndex = new Map<string, number>();
  logs.forEach((entry, index) => {
    if (!entry.batchId) {
      standalone.push({
        index,
        entry
      });
      return;
    }
    if (!batchMap.has(entry.batchId)) {
      batchMap.set(entry.batchId, []);
      batchFirstIndex.set(entry.batchId, index);
    }
    batchMap.get(entry.batchId)!.push(entry);
  });
  const items: {
    index: number;
    group: LogGroup;
  }[] = [];
  for (const {
    index,
    entry
  } of standalone) {
    items.push({
      index,
      group: {
        type: "standalone",
        entry
      }
    });
  }
  for (const [batchId, entries] of Array.from(batchMap.entries())) {
    if (entries.length === 1) {
      items.push({
        index: batchFirstIndex.get(batchId)!,
        group: {
          type: "standalone",
          entry: entries[0]
        }
      });
    } else {
      items.push({
        index: batchFirstIndex.get(batchId)!,
        group: {
          type: "batch",
          entries,
          batchId
        }
      });
    }
  }

  // Sort chronologically (ASC) so reconstruction can replay forward.
  // The API returns logs in DESC order, so we sort by the reference
  // entry's createdDate to guarantee chronological ordering.
  items.sort((a, b) => {
    const aEntry = a.group.type === "standalone" ? a.group.entry : a.group.entries[0];
    const bEntry = b.group.type === "standalone" ? b.group.entry : b.group.entries[0];
    return new Date(aEntry.createdDate).getTime() - new Date(bEntry.createdDate).getTime();
  });
  return items.map(i => i.group);
}
export function useGroupedLogs(logs) {
  const $ = _c(2);
  let t0;
  if ($[0] !== logs) {
    t0 = groupLogs(logs);
    $[0] = logs;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}